/**
 * Wat de leerkracht aan de server vraagt: één functie per /teacher-endpoint.
 *
 * Op het scherm heten ze groepen en lessen; in de paden classes en
 * sessions. Een "afwijking" is een instelling die voor één leerling in één
 * les anders is dan voor de groep (niveau, aantal vragen). De lengte van de
 * tekst hoort sinds stap 18 bij het niveau.
 */
import { verzoek } from './client';

const post = (pad, json) => verzoek(pad, { method: 'POST', json });
const put = (pad, json) => verzoek(pad, { method: 'PUT', json });
const weg = (pad) => verzoek(pad, { method: 'DELETE' });

export const leerkrachtApi = {
  // groepen en leerlingen
  haalGroepen: () => verzoek('/teacher/classes'),
  maakGroep: (naam, leerjaar) => post('/teacher/classes', { naam, leerjaar }),
  verwijderGroep: (groepId) => weg(`/teacher/classes/${groepId}`),
  haalStandaardniveau: (groepId) => verzoek(`/teacher/classes/${groepId}/default-niveau`),
  haalStandaarden: (groepId) => verzoek(`/teacher/classes/${groepId}/defaults`),
  haalLeerlingen: (groepId) => verzoek(`/teacher/classes/${groepId}/students`),
  maakLeerling: (groepId, leerling) => post(`/teacher/classes/${groepId}/students`, leerling),
  verwijderLeerling: (leerlingId) => weg(`/teacher/students/${leerlingId}`),
  // tempo: 'gewoon' of 'rustig'; weggelaten blijft het zoals het was
  zetNiveau: (leerlingId, niveau) => put(`/teacher/students/${leerlingId}/niveau`, { niveau }),
  /** De woordenoefening (stap 29): 'passief' (kiezen uit vier plaatjes) of 'actief' (de zin zeggen). */
  zetWoordenstand: (leerlingId, stand) => put(`/teacher/students/${leerlingId}/woordenstand`, { stand }),
  zetVoorlezen: (leerlingId, aan, tempo) => put(`/teacher/students/${leerlingId}/voorlezen`, { voorlezen: aan, ...(tempo ? { tempo } : {}) }),
  /** In welke moedertalen de vertaling kan worden voorgelezen: { aan: [...], mogelijk: [...] }. */
  haalVertalingVoorlezen: () => verzoek('/teacher/vertaling-voorlezen'),

  // teksten bekijken
  haalTeksten: () => verzoek('/teacher/texts'),
  haalTekst: (tekstId) => verzoek(`/teacher/texts/${tekstId}`),
  haalToetsenBijTekst: (tekstId) => verzoek(`/teacher/texts/${tekstId}/quizzes`),
  haalTekstOpNiveau: (tekstId, niveau) => verzoek(`/teacher/texts/${tekstId}/preview?niveau=${encodeURIComponent(niveau)}`),

  // lessen
  haalLessen: () => verzoek('/teacher/sessions'),
  maakLes: (les) => post('/teacher/sessions', les),
  zetLesAanOfUit: (lesId) => put(`/teacher/sessions/${lesId}/toggle`),
  verwijderLes: (lesId) => weg(`/teacher/sessions/${lesId}`),
  haalNiveausInLes: (lesId) => verzoek(`/teacher/sessions/${lesId}/niveaus`),
  haalLesTekst: (lesId, niveau) => verzoek(`/teacher/sessions/${lesId}/preview/text?niveau=${encodeURIComponent(niveau)}`),
  /** De toets op een niveau; zonder niveau: dat van de les. */
  haalLesToets: (lesId, niveau) => verzoek(`/teacher/sessions/${lesId}/preview/quiz${niveau ? `?niveau=${encodeURIComponent(niveau)}` : ''}`),
  /** De toets opnieuw, op het niveau dat in het voorbeeld gekozen is (de tekst maakt de beheerder). */
  maakToetsOpnieuw: (lesId, niveau) => post(`/teacher/sessions/${lesId}/regenerate?niveau=${encodeURIComponent(niveau)}`),

  // de groepjes van een groepsles (stap 23)
  haalGroepjes: (lesId) => verzoek(`/teacher/sessions/${lesId}/groepjes`),
  deelOpnieuwIn: (lesId) => post(`/teacher/sessions/${lesId}/groepjes/opnieuw`),
  /** Een leerling naar een ander groepje en/of deel: { groepje, deel }. */
  zetPlek: (lesId, leerlingId, plek) => put(`/teacher/sessions/${lesId}/groepjes/${leerlingId}`, plek),

  // afwijkingen per leerling
  haalLeerlingenInLes: (lesId) => verzoek(`/teacher/sessions/${lesId}/students`),
  bewaarAfwijking: (lesId, leerlingId, afwijking) => put(`/teacher/sessions/${lesId}/students/${leerlingId}`, afwijking),
  wisAfwijkingen: (lesId, leerlingId) => weg(`/teacher/sessions/${lesId}/students/${leerlingId}/overrides`),

  // de papieren toets voor één leerling (stap 24)
  haalPapierenToetsen: (leerlingId) => verzoek(`/teacher/students/${leerlingId}/papieren-toetsen`),
  /** { text_id, niveau, meerkeuze, open }; het model maakt de vragen (tot een minuut). */
  maakPapierenToets: (leerlingId, toets) =>
    verzoek(`/teacher/students/${leerlingId}/papieren-toetsen`, { method: 'POST', json: toets, wachtMs: 180000 }),
  /** De toets voor de leerling als pdf (blob), of met `nakijkblad` het nakijkblad. */
  haalPapierenToetsPdf: (toetsId, nakijkblad) =>
    verzoek(`/teacher/papieren-toetsen/${toetsId}/pdf${nakijkblad ? '?nakijkblad=true' : ''}`, { als: 'blob' }),
  verwijderPapierenToets: (toetsId) => weg(`/teacher/papieren-toetsen/${toetsId}`),
  /** Wat leerkracht en leerling samen besloten na het nakijken: 'hoger' of 'blijft'. */
  legBesluitVast: (toetsId, besluit) => put(`/teacher/papieren-toetsen/${toetsId}/besluit`, { besluit }),

  // resultaten en ontwikkeling
  haalResultaten: () => verzoek('/teacher/results'),
  verwijderResultaat: (leerlingLesId) => weg(`/teacher/results/${leerlingLesId}`),
  haalOntwikkeling: () => verzoek('/teacher/development'),
};
